import { capitalize } from "@prismicio/editor-support/String"
import { rawAddIcon, tableFieldStyles } from "@prismicio/editor-ui"
import type { TableOptions as TTTableOptions } from "@tiptap/extension-table"
import { TextSelection, type Transaction } from "@tiptap/pm/state"
import {
  CellSelection,
  columnResizingPluginKey,
  selectedRect,
  type TableRect,
  TableView as TTTableView,
} from "@tiptap/pm/tables"
import type { EditorView, ViewMutationRecord } from "@tiptap/pm/view"
import { Editor, type NodeViewRendererProps } from "@tiptap/react"

import { createButtonElement, renderHtmlFromString } from "./TableCell/utils"
import { findCell } from "./utils"

export interface TableOptions extends TTTableOptions {
  defaultCellMinWidth: number
}

/**
 * Renders a custom table, with a container for the table and the add
 * row/column buttons, while keeping the default table view for the
 * table content.
 */
export class TableView extends TTTableView {
  private tableWrapper: HTMLDivElement
  private selectionOverlay: TableSelectionOverlay

  constructor(props: NodeViewRendererProps, options: TableOptions) {
    const { node, editor } = props
    const { defaultCellMinWidth } = options
    super(node, defaultCellMinWidth)

    // dom is a div with a 'tableWrapper' class created in the parent TableView
    this.tableWrapper = this.dom

    // create containers to hold the table and the add buttons
    const container = document.createElement("div")
    container.setAttribute("data-testid", "table-container")
    container.className = tableFieldStyles.tableWithControls

    // This container allows the table and the controls to shrink and expand
    // with the table, even when the width of the columns occupies less space
    // than the container.
    // See https://linear.app/prismic/issue/DT-2644/
    const tableShrinkContainer = document.createElement("div")
    tableShrinkContainer.classList.add(tableFieldStyles.tableShrinkContainer)

    // create the selection overlay view
    this.selectionOverlay = new TableSelectionOverlay(editor, this.table)
    this.dom.appendChild(this.selectionOverlay.view)

    // create add column button
    if (props.editor.options.editable) {
      this.dom.appendChild(
        createAddButton({
          editor,
          type: "column",
          onAfterCommand: () => (container.scrollLeft = container.scrollWidth),
        }),
      )
    }

    tableShrinkContainer.appendChild(this.dom)
    container.appendChild(tableShrinkContainer)

    // create add row button
    if (props.editor.options.editable) {
      tableShrinkContainer.appendChild(createAddButton({ editor, type: "row" }))
    }

    this.dom = container // pass everything to the table view dom node
  }

  public destroy() {
    this.selectionOverlay.destroy()
  }

  override ignoreMutation(mutation: ViewMutationRecord): boolean {
    const target = mutation.target as HTMLElement
    // ignore mutations outside table wrappers/containers
    if (
      target !== this.tableWrapper &&
      "closest" in target &&
      target.closest?.(".tableWrapper")
    ) {
      return true
    }

    return super.ignoreMutation(mutation)
  }
}

interface EditorUpdateState {
  editor: Editor
  transaction: Transaction
}

class TableSelectionOverlay {
  public view: HTMLElement

  private selectionAnchorCell: HTMLElement | null = null
  private selectionHeadCell: HTMLElement | null = null

  constructor(
    private readonly editor: Editor,
    private readonly table: HTMLTableElement,
  ) {
    // create the selection overlay view
    const selectionOverlay = document.createElement("div")
    selectionOverlay.setAttribute("contentEditable", "false")
    selectionOverlay.classList.add(tableFieldStyles.tableSelectionOverlay)
    selectionOverlay.setAttribute("aria-label", "Table selection overlay")
    this.view = selectionOverlay

    editor.on("transaction", this.onTransaction)
    editor.on("focus", this.onFocus)
    editor.on("blur", this.onBlur)
    editor.on("selectionUpdate", this.onSelectionUpdate)
    this.connectTableSizeObservers()
  }

  public destroy() {
    this.editor.off("transaction", this.onTransaction)
    this.editor.off("focus", this.onFocus)
    this.editor.off("blur", this.onBlur)
    this.editor.off("selectionUpdate", this.onSelectionUpdate)
    this.clearSelectionOverlay()
    this.disconnectTableSizeObservers()
  }

  /**
   * React to resizing transactions and update the selection overlay.
   */
  private onTransaction = (state: EditorUpdateState) => {
    const resizePluginMeta = state.transaction.getMeta(
      columnResizingPluginKey,
    ) as Record<string, unknown>
    // Detect if the transaction was a column resize starting or ending.
    // https://github.com/ProseMirror/prosemirror-tables/blob/v1.6.4/src/columnresizing.ts#L211
    if (resizePluginMeta && "setDragging" in resizePluginMeta) {
      // Force focus to ensure consistency with the focus style when the user
      // clicks outside of the editor, and then inside on a resize handle. There
      // should be no need for this, as the user already clicked on the editor,
      // but it's not firing the focus event.
      state.editor.commands.focus()

      this.onSelectionUpdate()
    }
  }

  private onFocus = () => {
    // When we have a cell selected (focused or writing something) and click
    // outside the editor, the selection is kept, but the focus is lost.
    // When we focus again, the focus event is fired, but there is no selection
    // update event triggered, because it was never lost. Because of this, we
    // need to manually trigger the selection update to ensure the selection
    // overlay is displayed again to match the focus state.
    this.onSelectionUpdate()
  }

  private onBlur = () => {
    // Used in combination with onFocus, here we ensure that if the editor is
    // blurred, the selection overlay is hidden, to match the focus state.
    // We should only reset if we're in a focused state, not cell selection,
    // which is when head and anchor have the same reference.
    if (this.selectionHeadCell === this.selectionAnchorCell) {
      this.clearSelectionOverlay()
    }
  }

  /**
   * React to selection updates and recreate the overlay for the new cells.
   */
  private onSelectionUpdate = () => {
    const { selection } = this.editor.state

    let headCell: HTMLElement | null = null
    let anchorCell: HTMLElement | null = null

    if (selection instanceof TextSelection) {
      const parentCell = findCell(selection)
      headCell = anchorCell = getNodeDom(this.editor.view, parentCell?.pos)
    } else if (selection instanceof CellSelection) {
      headCell = getNodeDom(this.editor.view, selection.$headCell.pos)
      anchorCell = getNodeDom(this.editor.view, selection.$anchorCell.pos)
    } else {
      this.clearSelectionOverlay()
    }

    if (!anchorCell || !headCell) return

    this.selectionAnchorCell = anchorCell
    this.selectionHeadCell = headCell

    this.renderSelectionOverlay()
  }

  private clearSelectionOverlay() {
    const { style } = this.view
    style.width = `0px`
    style.height = `0px`
    requestAnimationFrame(() => {
      style.display = "none"
    })

    this.selectionAnchorCell = null
    this.selectionHeadCell = null
  }

  private renderSelectionOverlay() {
    if (!this.selectionAnchorCell || !this.selectionHeadCell) return

    // Get the bounding rectangles
    const anchorRect = this.selectionAnchorCell.getBoundingClientRect()
    const headRect = this.selectionHeadCell.getBoundingClientRect()
    const tableRect = this.table.getBoundingClientRect()

    // Calculate the selection area
    const left = Math.min(anchorRect.left, headRect.left) - tableRect.left
    const top = Math.min(anchorRect.top, headRect.top) - tableRect.top
    const right = Math.max(anchorRect.right, headRect.right) - tableRect.left
    const bottom = Math.max(anchorRect.bottom, headRect.bottom) - tableRect.top

    const rect = selectedRect(this.editor.state)
    const borderSpacing = getBorderSpacing(rect)

    // Set the overlay position and size
    const { style } = this.view
    style.left = `${left}px`
    style.top = `${top}px`
    style.width = `${right - left + borderSpacing.width}px`
    style.height = `${bottom - top + borderSpacing.height}px`
    style.borderRadius = getBorderRadius(rect)

    // ensure position is updated before showing
    requestAnimationFrame(() => {
      style.display = "block"
    })
  }

  private tableHeightObserver: ResizeObserver | null = null
  private tableMinWidthObserver: MutationObserver | null = null
  private previousTableHeight: number | null = null

  private connectTableSizeObservers() {
    this.disconnectTableSizeObservers()

    // Observe table height changes
    // Covers individual cell size changes (e.g. multiline text that gets
    // cleared, which changes the cell's height).
    this.tableHeightObserver ??= new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newHeight = entry.contentRect.height
        if (newHeight === this.previousTableHeight) continue
        // update if the height has changed
        this.renderSelectionOverlay()
        this.previousTableHeight = newHeight
      }
    })
    // Observe table style prop changes.
    // Covers most of the updates, as most actions change the table min-width
    // (e.g. adding/deleting a row or column, resizing cells (including not
    // selected ones), etc).
    this.tableMinWidthObserver ??= new MutationObserver(() => {
      // update right away, as we're only observing the style attribute
      this.renderSelectionOverlay()
    })

    this.tableHeightObserver.observe(this.table)
    this.tableMinWidthObserver.observe(this.table, {
      attributes: true,
      attributeFilter: ["style"],
    })
  }

  private disconnectTableSizeObservers() {
    this.tableHeightObserver?.disconnect()
    this.tableMinWidthObserver?.disconnect()
  }
}

function getNodeDom(view: EditorView, pos?: number): HTMLElement | null {
  if (!pos) return null
  const node = view.nodeDOM(pos)
  return node instanceof HTMLElement ? node : null
}

function getBorderSpacing(rect: TableRect) {
  // If the selection doesn't span the whole height of the table, we need to
  // add 1 to the height to account for the border. Same for the width, if the
  // selection doesn't span the whole width of the table. This is because the
  // cells don't have a bottom border, only top, except the bottom-most and
  // right-most cells. This way, the overlay is perfectly drawn over the
  // border of the selected cells.
  let { width, height } = { width: 1, height: 1 }
  if (rect.right === rect.map.width) width = 0
  if (rect.bottom === rect.map.height) height = 0
  return { width, height }
}

function getBorderRadius(rect: TableRect) {
  // This applies a border radius to the edges of the overlay when the selection
  // reaches the corners of the table.
  let { tl, tr, br, bl } = { tl: 2, tr: 2, br: 2, bl: 2 }
  if (rect.top === 0) {
    if (rect.left === 0) tl = 6
    if (rect.right === rect.map.width) tr = 6
  }
  if (rect.bottom === rect.map.height) {
    if (rect.right === rect.map.width) bl = 6
    if (rect.left === 0) br = 6
  }
  return `${tl}px ${tr}px ${bl}px ${br}px`
}

function createAddButton(args: {
  type: "row" | "column"
  editor: Editor
  onAfterCommand?: () => void
}) {
  const { type, editor, onAfterCommand } = args

  const capitalizedType = capitalize(type)
  const button = createButtonElement({
    title: `Add ${type}`,
    className: tableFieldStyles[`addTable${capitalizedType}Button`],
    contentEditable: false,
    onClick: () => {
      editor.commands[`add${capitalizedType}ToEnd`]()
      onAfterCommand?.()
    },
  })

  const icon = renderHtmlFromString(rawAddIcon)
  if (icon) button.appendChild(icon)

  return button
}
